/** @jsx jsx */
import React from 'react';
import PropTypes from 'prop-types';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { ThemeConsumer } from '@cvent/carina-theme-provider';
import Button from '@cvent/carina-button';
import {
  XIcon,
  AlertCircleIcon,
  AlertTriangleIcon,
  InfoIcon,
  CheckCircleIcon
} from '@cvent/carina-icon';
import { jsx } from '@emotion/core';
import {
  getAlertStyles,
  getIconColStyles,
  getContainerStyles,
  getBodyColStyles,
  getMessageColStyles,
  getHeadingStyles,
  getButtonColStyles,
  getDismissColStyles,
  getDismissButtonStyles,
  getContentColStyles
} from './util/styles';

/**
 * Displays an important message.
 */
export default class Alert extends React.Component {
  static propTypes = {
    /** Visual importance/prominence */
    appearance: PropTypes.oneOf(['danger', 'warning', 'info', 'success']),
    /** Button placement below message */
    buttonStacked: PropTypes.bool,
    /** Button label */
    buttonText: PropTypes.string,
    /** Interval for self-dismissal (user-level variant only) */
    dismissInterval: PropTypes.number,
    /** Alert heading */
    heading: PropTypes.string,
    /** Forces position relative */
    isInline: PropTypes.bool,
    /** Text direction */
    isRtl: PropTypes.bool,
    /** Max-width for Alert children */
    maxWidth: PropTypes.string,
    /** Button interaction event handler */
    onButtonPress: PropTypes.func,
    /** Dismiss button interaction event handler */
    onDismiss: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    /** For e2e tests (maps to `data-cvent-id`) */
    testID: PropTypes.string,
    /** Alert message. Pass in either a node containing valid jsx or a string */
    text: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    /** the function that gets called when the alert goes away (closed or the timeout has been called) */
    onVanish: PropTypes.func
  };

  static defaultProps = {
    appearance: 'info',
    dismissInterval: 4,
    isRtl: false
  };

  constructor(props) {
    super(props);
    this.state = {
      opacity: 0,
      isDismissed: false
    };
  }

  componentDidMount() {
    this.opacityTimer = setTimeout(() => {
      this.setState({ opacity: 1 });
    }, 10);
    if (this.props.variant === 'user' && !this.props.isInline) {
      this.dismissTimer = setTimeout(() => {
        this.handleCloseButtonClick();
      }, this.props.dismissInterval * 1000);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.opacityTimer);
    clearTimeout(this.dismissTimer);
    clearTimeout(this.incrementTimeout);
    if (typeof this.props.onVanish === 'function') {
      this.props.onVanish();
    }
  }
  render() {
    const {
      heading,
      text,
      appearance,
      isRtl,
      testID,
      buttonText,
      onButtonPress,
      variant,
      onDismiss,
      dismissInterval,
      isInline,
      onIncrementChild,
      buttonStacked,
      maxWidth: propMaxWidth,
      ...propDrop
    } = this.props;

    const isDismissed = this.state.isDismissed;
    const opacity = this.state.opacity;
    const maxWidth =
      variant === 'user' &&
      (!propMaxWidth || propMaxWidth === 'unset' || propMaxWidth === '')
        ? '560px'
        : propMaxWidth;

    return (
      <ThemeConsumer>
        {theme => (
          <React.Fragment>
            {!isDismissed && (
              <div
                {...propDrop}
                // the aria role. Even though the component is named alert, alert is not the correct role for everything
                // If the role is set to alert, then it takes the assertive live property, meaning it will interrupt.
                // That should only be the case for errors, so this check is made so that it is polite in most cases.
                // note: no aria label here because it is not needed: the screen reader will pick up the role,
                // and then the content inside of the alert and is able to formulate a better label.
                // If one were to be explicitly declared here, then both it could be redundant/verbose,
                // as well as confuse non-english users (because, let's face it, I'm not going to put in a whole bunch
                // of translation stuff here, and the default language is English: the lingua Franca of the internet.)
                role={variant === 'error' ? 'alert' : 'status'}
                css={getAlertStyles({
                  appearance,
                  theme,
                  isRtl,
                  opacity,
                  variant,
                  maxWidth
                })}
                {...injectTestId(testID)}
              >
                <div css={getBodyColStyles({ variant })}>
                  <div css={getContainerStyles({ variant })}>
                    <div
                      css={getIconColStyles({
                        appearance,
                        theme,
                        isRtl,
                        variant
                      })}
                    >
                      {insertAlertIcon(appearance)}
                    </div>
                    <div
                      css={getContentColStyles({
                        theme,
                        isRtl,
                        variant
                      })}
                    >
                      <div
                        css={getMessageColStyles({
                          theme,
                          isRtl,
                          buttonStacked,
                          dismissible:
                            typeof onDismiss === 'function' ||
                            onDismiss === true,
                          buttonText,
                          variant
                        })}
                      >
                        {heading && (
                          <span
                            css={getHeadingStyles({
                              appearance,
                              theme,
                              text,
                              variant
                            })}
                          >
                            {heading}
                          </span>
                        )}
                        {text}
                      </div>
                      {buttonText && variant !== 'app' && (
                        <div
                          css={{
                            justifySelf: 'stretch',
                            width: 'auto',
                            minWidth: '0px',
                            flex: '1 1 1px'
                          }}
                        ></div>
                      )}
                      {buttonText && (
                        <div
                          css={getButtonColStyles({
                            appearance,
                            theme,
                            isRtl,
                            dismissible:
                              typeof onDismiss === 'function' ||
                              onDismiss === true,
                            variant
                          })}
                        >
                          <Button
                            appearance="primary"
                            variant={appearance !== 'info' ? appearance : ''}
                            isRtl={isRtl}
                            onClick={onButtonPress}
                            text={buttonText}
                            size="s"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {(typeof onDismiss === 'function' || onDismiss === true) && (
                  <div css={getDismissColStyles({ isRtl })}>
                    <button
                      css={getDismissButtonStyles({
                        appearance,
                        theme,
                        variant
                      })}
                      type="button"
                      onClick={() => {
                        if (typeof onDismiss === 'function') {
                          onDismiss();
                        }
                        this.handleCloseButtonClick();
                      }}
                    >
                      <XIcon size="l" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </React.Fragment>
        )}
      </ThemeConsumer>
    );
  }

  handleCloseButtonClick = () => {
    this.setState({ opacity: 0 });
    this.incrementTimeout = setTimeout(() => {
      this.props.onIncrementChild();
      this.setState({ isDismissed: true });
    }, 300);
  };
}

function insertAlertIcon(appearance) {
  let iconMap = {
    danger: AlertCircleIcon,
    warning: AlertTriangleIcon,
    info: InfoIcon,
    success: CheckCircleIcon
  };

  let IconElement = iconMap[appearance];

  return <IconElement size="l" />;
}
